/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { Stack } from '@fluentui/react/lib/Stack';
import { IToggleProps, IToggleStyles, Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { noop } from 'src/core/utils/base';

export type TestSetDetailsCommandBarProps = {
    query: string;
    isLoading?: boolean;
    isInEmptyState?: boolean;
    incorrectToggleMessage?: string;
    showIncorrectPredictions: boolean;
    onFilter?: () => void;
    onQueryChange: (value: string) => void;
    onChangeShowIncorrectPredictions: () => void;
    getFarItems?: (defaultFarItems: ICommandBarItemProps[]) => ICommandBarItemProps[];
};

const messages = defineMessages({
    none: { id: 'modelGuidance.testSetDetailsCommandBar.none', defaultMessage: 'None' },
    model: { id: 'modelGuidance.testSetDetailsCommandBar.model', defaultMessage: 'Model' },
    filter: { id: 'modelGuidance.testSetDetailsCommandBar.filter', defaultMessage: 'Filter' },
    searchPlaceholder: { id: 'modelGuidance.testSetDetailsCommandBar.searchPlaceholder', defaultMessage: 'Search' },
    showConflicts: { id: 'modelGuidance.testSetDetailsCommandBar.showConflicts', defaultMessage: 'Show mismatches only' },
    modelsDropDownAriaLabel: { id: 'modelGuidance.testSetDetailsCommandBar.modelsDropDownAriaLabel', defaultMessage: 'Models' }
});

const toggleStyles = classNamesFunction<IToggleProps, IToggleStyles>()({
    container: { margin: '5px 0 0 10px' },
    label: { fontSize: '14px', fontWeight: 400 },
    root: { marginBottom: '0px', marginRight: '5px', display: 'flex' }
});

export const TestSetDetailsCommandBar = observer((props: TestSetDetailsCommandBarProps) => {
    const { localize } = useLocalization();
    const {
        query,
        isLoading = false,
        isInEmptyState = false,
        showIncorrectPredictions,
        incorrectToggleMessage = localize(messages.showConflicts),
        onQueryChange,
        getFarItems = noop,
        onFilter = undefined,
        onChangeShowIncorrectPredictions
    } = props;

    const items: ICommandBarItemProps[] = [
        {
            key: 'filterIncorrectPredictions',
            onRender: () =>
                isLoading ? (
                    <Shimmer
                        shimmerElements={[{ type: ShimmerElementType.line, width: '70px', height: 30 }]}
                        styles={{ root: { marginTop: '2px', marginRight: '18px' } }}
                    />
                ) : (
                    <Stack horizontal role="menuitem" verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <Toggle
                            role="switch"
                            styles={toggleStyles}
                            label={incorrectToggleMessage}
                            checked={showIncorrectPredictions}
                            ariaLabel={incorrectToggleMessage}
                            disabled={isInEmptyState || isLoading}
                            onChange={onChangeShowIncorrectPredictions}
                        />
                    </Stack>
                )
        }
    ];

    const defaultFarItems: ICommandBarItemProps[] = [
        {
            key: 'search',
            onRender: () => (
                <LuisDebouncedCollapsableSearchBox
                    value={query}
                    alwaysExpanded
                    ariaLabel={localize(messages.searchPlaceholder)}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => onQueryChange(newValue)}
                />
            )
        },
        ...(onFilter ? [{ key: 'filter', onClick: onFilter, text: localize(messages.filter), iconProps: { iconName: 'Filter' } }] : [])
    ];

    return <LuisCommandBar items={items} farItems={getFarItems?.(defaultFarItems) || defaultFarItems} showTopBorder />;
});
